import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  fetchCasaMapaAdminListingDataHealth,
  fetchCasaMapaAdminListingRunLogs,
  runCasaMapaAdminListingSource,
} from "../../../api/clientCasamapaAdminApi";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useLocale } from "../../../intl";
import { formatDetailDateTime } from "../../../utils/propertyDetailFormat";
import "../../../styles/casamapaAdminPortal.css";
import "../../../styles/casamapaListingHealthDashboard.css";
import "../../../styles/casamapaListingDataHealth.css";

const COPY = "realEstate.casaMapa.admin.listings";
const HEALTH_COPY = `${COPY}.dataHealth`;

const TERMINAL_RUN_STATUSES = new Set([
  "SUCCESS",
  "FAILED",
  "CANCELLED",
  "COMPLETED",
  "COMPLETED_WITH_ERRORS",
]);

function statusTone(status) {
  const normalized = String(status || "").toUpperCase();
  if (["SUCCESS", "COMPLETED"].includes(normalized)) return "success";
  if (["FAILED", "CANCELLED"].includes(normalized)) return "danger";
  if (["COMPLETED_WITH_ERRORS", "WARNING"].includes(normalized)) return "warn";
  if (["RUNNING", "QUEUED", "IN_PROGRESS"].includes(normalized)) return "active";
  return "neutral";
}

function StatusBadge({ status, label }) {
  const tone = statusTone(status);
  return <span className={`cm-data-health__status cm-data-health__status--${tone}`}>{label || status || "—"}</span>;
}

function LogLevelBadge({ level }) {
  const normalized = String(level || "info").toLowerCase();
  return <span className={`cm-data-health__log-level cm-data-health__log-level--${normalized}`}>{normalized}</span>;
}

function SourceCard({
  source,
  t,
  runningSourceKey,
  onRun,
  onSelectRun,
  selectedRunId,
}) {
  const isRunning = runningSourceKey === source.sourceKey || source.isRunning;

  return (
    <article className="cm-data-health__source-card">
      <div className="cm-data-health__source-head">
        <div>
          <h3>{source.displayName}</h3>
          <p className="muted">
            <code>{source.sourceKey}</code>
            {source.agencySlug ? (
              <>
                {" · "}
                <Link to={`../agencies/${source.agencySlug}`}>{source.agencySlug}</Link>
              </>
            ) : null}
          </p>
        </div>
        <div className="cm-data-health__source-actions">
          <StatusBadge
            status={source.lastStatus}
            label={
              source.isRunning
                ? t(`${HEALTH_COPY}.sources.running`)
                : source.lastStatus || t(`${HEALTH_COPY}.sources.neverRun`)
            }
          />
          <button
            type="button"
            className="cm-listing-health__fix-button"
            disabled={!source.supportsRunNow || isRunning}
            onClick={() => onRun(source)}
          >
            {isRunning ? t(`${HEALTH_COPY}.sources.runningAction`) : t(`${HEALTH_COPY}.sources.runAction`)}
          </button>
        </div>
      </div>

      <dl className="cm-data-health__meta">
        <div>
          <dt>{t(`${HEALTH_COPY}.sources.lastRun`)}</dt>
          <dd>{source.lastRunAt ? formatDetailDateTime(source.lastRunAt) : "—"}</dd>
        </div>
        <div>
          <dt>{t(`${HEALTH_COPY}.sources.lastSuccess`)}</dt>
          <dd>{source.lastSuccessAt ? formatDetailDateTime(source.lastSuccessAt) : "—"}</dd>
        </div>
        <div>
          <dt>{t(`${HEALTH_COPY}.sources.enabled`)}</dt>
          <dd>{source.enabled ? t(`${HEALTH_COPY}.yes`) : t(`${HEALTH_COPY}.no`)}</dd>
        </div>
        <div>
          <dt>{t(`${HEALTH_COPY}.sources.registered`)}</dt>
          <dd>{source.registered ? t(`${HEALTH_COPY}.yes`) : t(`${HEALTH_COPY}.no`)}</dd>
        </div>
      </dl>

      {source.lastErrorMessage ? (
        <p className="cm-data-health__error-banner">{source.lastErrorMessage}</p>
      ) : null}

      {source.recentRuns?.length ? (
        <div className="cm-data-health__runs">
          <h4>{t(`${HEALTH_COPY}.runs.title`)}</h4>
          <ul className="cm-data-health__run-list">
            {source.recentRuns.map((run) => (
              <li key={run.runId}>
                <button
                  type="button"
                  className={`cm-data-health__run-button${
                    selectedRunId === run.runId ? " cm-data-health__run-button--active" : ""
                  }`}
                  onClick={() => onSelectRun(run)}
                >
                  <span className="cm-data-health__run-main">
                    <StatusBadge status={run.status} label={run.status} />
                    <time>{run.startedAt ? formatDetailDateTime(run.startedAt) : "—"}</time>
                  </span>
                  <span className="muted">
                    {t(`${HEALTH_COPY}.runs.records`, {
                      received: run.recordsFound ?? 0,
                      failed: run.recordsFailed ?? 0,
                    })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="muted">{t(`${HEALTH_COPY}.runs.empty`)}</p>
      )}
    </article>
  );
}

function RunLogPanel({ run, logs, logCounts, loading, error, t, levelFilter, onLevelFilter }) {
  return (
    <section className="cm-data-health__logs">
      <header className="cm-data-health__logs-head">
        <div>
          <h2>{t(`${HEALTH_COPY}.logs.title`)}</h2>
          {run ? (
            <p className="muted">
              <StatusBadge status={run.status} label={run.status} />
              {run.startedAt ? ` · ${formatDetailDateTime(run.startedAt)}` : ""}
            </p>
          ) : (
            <p className="muted">{t(`${HEALTH_COPY}.logs.selectRun`)}</p>
          )}
        </div>
        <div className="cm-data-health__log-filters">
          {["all", "error", "warn", "info"].map((level) => (
            <button
              key={level}
              type="button"
              className={`cm-data-health__filter-chip${
                levelFilter === level ? " cm-data-health__filter-chip--active" : ""
              }`}
              onClick={() => onLevelFilter(level)}
            >
              {t(`${HEALTH_COPY}.logs.level.${level}`)}
              {level === "error" && logCounts?.errors != null ? ` (${logCounts.errors})` : ""}
              {level === "warn" && logCounts?.warnings != null ? ` (${logCounts.warnings})` : ""}
            </button>
          ))}
        </div>
      </header>

      {error ? <p className="admin-data-health-error">{error}</p> : null}
      {loading ? <p className="muted">{t(`${HEALTH_COPY}.logs.loading`)}</p> : null}

      {!loading && run && logs?.length ? (
        <ol className="cm-data-health__log-list">
          {logs.map((entry) => (
            <li key={entry.id} className={`cm-data-health__log-item cm-data-health__log-item--${entry.level}`}>
              <div className="cm-data-health__log-top">
                <LogLevelBadge level={entry.level} />
                <span className="cm-data-health__log-step">{entry.step}</span>
                <time>{entry.createdAt ? formatDetailDateTime(entry.createdAt) : ""}</time>
              </div>
              <p>{entry.message}</p>
              {entry.context && Object.keys(entry.context).length ? (
                <pre className="cm-data-health__log-context">
                  {JSON.stringify(entry.context, null, 2)}
                </pre>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}

      {!loading && run && !logs?.length ? (
        <p className="muted">{t(`${HEALTH_COPY}.logs.empty`)}</p>
      ) : null}
    </section>
  );
}

export default function CasaMapaAdminListingDataHealthPage() {
  const { listingId } = useParams();
  const { path, clientViewId } = useCasaMapaAdminPortal();
  const { t } = useLocale();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [runningSourceKey, setRunningSourceKey] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logCounts, setLogCounts] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");

  const loadHealth = useCallback(async () => {
    if (!clientViewId || !listingId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const result = await fetchCasaMapaAdminListingDataHealth(clientViewId, listingId);
      setHealth(result.health || null);
    } catch (err) {
      setError(err.message || t(`${HEALTH_COPY}.loadError`));
      setHealth(null);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, listingId, t]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  const loadLogs = useCallback(
    async (run, filter = levelFilter) => {
      if (!clientViewId || !listingId || !run?.runId) {
        setLogs([]);
        setLogCounts(null);
        return;
      }

      try {
        setLogsLoading(true);
        setLogsError("");
        const result = await fetchCasaMapaAdminListingRunLogs(clientViewId, listingId, run.runId, {
          limit: 300,
          level: filter === "all" ? undefined : filter,
        });
        setSelectedRun(result.run || run);
        setLogs(result.logs || []);
        setLogCounts(result.logCounts || null);
      } catch (err) {
        setLogsError(err.message || t(`${HEALTH_COPY}.logs.loadError`));
        setLogs([]);
      } finally {
        setLogsLoading(false);
      }
    },
    [clientViewId, listingId, levelFilter, t]
  );

  useEffect(() => {
    if (selectedRun?.runId) {
      loadLogs(selectedRun, levelFilter);
    }
  }, [levelFilter, loadLogs, selectedRun]);

  const pollRun = useCallback(
    async (runId, sourceKey) => {
      let attempts = 0;
      const maxAttempts = 60;

      const tick = async () => {
        attempts += 1;
        await loadHealth();

        const refreshed = (await fetchCasaMapaAdminListingDataHealth(clientViewId, listingId)).health;
        const source = refreshed?.sources?.find((row) => row.sourceKey === sourceKey);
        const run = source?.recentRuns?.find((row) => row.runId === runId);

        if (run && TERMINAL_RUN_STATUSES.has(String(run.status || "").toUpperCase())) {
          setRunningSourceKey(null);
          setSelectedRun(run);
          setActionMessage(t(`${HEALTH_COPY}.runComplete`, { status: run.status }));
          return;
        }

        if (attempts >= maxAttempts) {
          setRunningSourceKey(null);
          setActionMessage(t(`${HEALTH_COPY}.runPollingTimeout`));
          return;
        }

        window.setTimeout(tick, 4000);
      };

      window.setTimeout(tick, 4000);
    },
    [clientViewId, listingId, loadHealth, t]
  );

  const handleRunSource = async (source) => {
    if (!clientViewId || !listingId) return;

    try {
      setActionMessage("");
      setRunningSourceKey(source.sourceKey);
      const result = await runCasaMapaAdminListingSource(clientViewId, listingId, source.sourceKey);
      setActionMessage(result.message || t(`${HEALTH_COPY}.runQueued`));
      if (result.runId) {
        pollRun(result.runId, source.sourceKey);
      } else {
        setRunningSourceKey(null);
        await loadHealth();
      }
    } catch (err) {
      setRunningSourceKey(null);
      setActionMessage(err.message || t(`${HEALTH_COPY}.runFailed`));
    }
  };

  const listing = health?.listing;
  const issues = health?.issues || [];

  const brokerLinks = useMemo(() => health?.brokerLinks || [], [health]);

  if (!clientViewId) {
    return <p className="muted">{t("realEstate.casaMapa.admin.errors.noViewMessage")}</p>;
  }

  return (
    <section className="casamapa-admin-page cm-data-health">
      <header className="casamapa-admin-page__header cm-data-health__header">
        <div>
          <div className="eyebrow">{t("realEstate.casaMapa.admin.portalEyebrow")}</div>
          <h1>{listing?.title || t(`${HEALTH_COPY}.title`)}</h1>
          <p className="muted">{t(`${HEALTH_COPY}.subtitle`)}</p>
        </div>
        <div className="cm-data-health__header-actions">
          <Link className="casamapa-admin-link-button" to={path(`listings/${listingId}`)}>
            {t(`${HEALTH_COPY}.backToDetail`)}
          </Link>
          <Link className="casamapa-admin-link-button" to={path("listings")}>
            {t(`${COPY}.detail.backToList`)}
          </Link>
          <button type="button" className="casamapa-admin-link-button" onClick={loadHealth}>
            {t("realEstate.casaMapa.admin.refresh")}
          </button>
        </div>
      </header>

      {error ? <p className="admin-data-health-error">{error}</p> : null}
      {actionMessage ? <p className="cm-data-health__action-message">{actionMessage}</p> : null}
      {loading ? <p className="muted">{t(`${HEALTH_COPY}.loading`)}</p> : null}

      {!loading && health ? (
        <div className="cm-data-health__layout">
          <div className="cm-data-health__main">
            <section className="cm-listing-health__panel">
              <h2>{t(`${HEALTH_COPY}.issues.title`)}</h2>
              {issues.length ? (
                <div className="cm-listing-detail__issues">
                  {issues.map((issueId) => (
                    <span key={issueId} className="cm-listing-health__issue-tag">
                      {t(`${COPY}.issues.${issueId}`)}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="muted">{t(`${HEALTH_COPY}.issues.empty`)}</p>
              )}
            </section>

            <section className="cm-listing-health__panel">
              <h2>{t(`${HEALTH_COPY}.links.title`)}</h2>
              {brokerLinks.length ? (
                <ul className="cm-data-health__link-list">
                  {brokerLinks.map((link) => (
                    <li key={link.linkId}>
                      <strong>{link.source || "—"}</strong>
                      {link.isPrimary ? ` · ${t(`${HEALTH_COPY}.links.primary`)}` : ""}
                      {link.title ? ` · ${link.title}` : ""}
                      <span className="muted"> · {link.linkStatus}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">{t(`${HEALTH_COPY}.links.empty`)}</p>
              )}
            </section>

            <section className="cm-listing-health__panel">
              <div className="cm-data-health__section-head">
                <h2>{t(`${HEALTH_COPY}.sources.title`)}</h2>
                <p className="muted">{t(`${HEALTH_COPY}.sources.hint`)}</p>
              </div>

              {health.sources?.length ? (
                <div className="cm-data-health__sources">
                  {health.sources.map((source) => (
                    <SourceCard
                      key={source.sourceKey}
                      source={source}
                      t={t}
                      runningSourceKey={runningSourceKey}
                      onRun={handleRunSource}
                      onSelectRun={(run) => {
                        setSelectedRun(run);
                        setLevelFilter("all");
                      }}
                      selectedRunId={selectedRun?.runId}
                    />
                  ))}
                </div>
              ) : (
                <p className="muted">{t(`${HEALTH_COPY}.sources.empty`)}</p>
              )}
            </section>
          </div>

          <aside className="cm-data-health__aside">
            <RunLogPanel
              run={selectedRun}
              logs={logs}
              logCounts={logCounts}
              loading={logsLoading}
              error={logsError}
              t={t}
              levelFilter={levelFilter}
              onLevelFilter={setLevelFilter}
            />
          </aside>
        </div>
      ) : null}
    </section>
  );
}
